/**
 * RequiredPermitsBlock.jsx
 * Affiche une grille de cartes "Permis complexes nécessaires" en fond rouge
 * pour rappeler à l'utilisateur de compléter ces permis avant validation.
 * Les cartes apparaissent automatiquement en fonction des blocs E-O cochés.
 * Chaque carte affiche un mini-cercle de progression (% de complétion).
 */
import { AlertOctagon, FileWarning, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PERMIT_TYPES } from '@/constants/permits';

// Mapping bloc → types de permis requis (DOIT rester aligné avec backend BLOCK_TRIGGERS)
export const BLOCK_TRIGGERS = {
  E: ['bon_isolation_energies', 'attestation_consignation_elec'],
  F: ['permis_feu'],
  G: ['permis_levage'],
  H: ['permis_hauteur', 'permis_nacelle'],
  I: ['permis_espace_confine'],
  K: ['attestation_consignation_elec'],
  N: ['permis_bordure_eau'],
  O: ['permis_fouille'],
};

export function getRequiredPermitTypes(blocsRisques, opts = {}) {
  const { hasSubcontractor = false } = opts;
  const required = new Set();
  Object.entries(blocsRisques || {}).forEach(([blocId, items]) => {
    if (!Array.isArray(items)) return;
    if (items.some(Boolean)) {
      (BLOCK_TRIGGERS[blocId] || []).forEach((t) => {
        // Bloc E → attestation_consignation_elec uniquement si sous-traitant présent
        if (blocId === 'E' && t === 'attestation_consignation_elec' && !hasSubcontractor) return;
        required.add(t);
      });
    }
  });
  return [...required];
}

// Champs minimaux par type de permis complexe pour calculer le % de complétion
const REQUIRED_FIELDS_BY_TYPE = {
  permis_feu: ['extra.nature_point_chaud', 'extra.materiel_utilise', 'extra.surveillant_feu'],
  permis_levage: ['extra.engin_levage', 'extra.poids_charge', 'extra.nature_charge'],
  permis_hauteur: ['extra.type_protection', 'extra.hauteur_intervention'],
  permis_nacelle: ['extra.type_nacelle', 'extra.numero_nacelle'],
  permis_espace_confine: ['extra.identification_espace', 'extra.atmosphere_test'],
  permis_bordure_eau: ['extra.epi_flottants', 'extra.surveillant_eau'],
  permis_fouille: ['extra.profondeur', 'extra.dt_dict'],
  attestation_consignation_elec: ['extra.numero_pced', 'extra.chargeconsignation'],
  bon_isolation_energies: ['extra.lignes_isolation'],
};
// Champs partagés (héritage AT) : si remplis, on les compte aussi
const COMMON_FIELDS = ['titre', 'lieu', 'date_debut', 'date_fin', 'entreprise_exterieure'];

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function isFieldFilled(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return Boolean(v);
}

export function computeCompletion(permit) {
  if (!permit) return 0;
  const specific = REQUIRED_FIELDS_BY_TYPE[permit.type] || [];
  const allFields = [...COMMON_FIELDS, ...specific];
  if (allFields.length === 0) return 100;
  const filled = allFields.filter((f) => isFieldFilled(getNestedValue(permit, f))).length;
  return Math.round((filled / allFields.length) * 100);
}

function CompletionRing({ percent, size = 36 }) {
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  const color = percent === 100 ? '#16a34a' : percent >= 50 ? '#f59e0b' : '#DA291C';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e7e5e4" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.4s ease' }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center text-[9px] font-bold"
        style={{ color }}
      >
        {percent === 100 ? <CheckCircle2 className="w-4 h-4" /> : `${percent}%`}
      </div>
    </div>
  );
}

export default function RequiredPermitsBlock({ blocsRisques, linkedPermits = [], parentId, onOpen, entrepriseExterieure = '' }) {
  const ee = (entrepriseExterieure || '').trim();
  const hasSubcontractor = !!ee && ee.toLowerCase() !== 'travaux internes';
  const requiredTypes = getRequiredPermitTypes(blocsRisques, { hasSubcontractor });

  if (requiredTypes.length === 0) {
    return (
      <div className="border-2 border-dashed border-stone-300 p-6 text-center bg-stone-50">
        <FileWarning className="w-8 h-8 text-stone-400 mx-auto mb-2" />
        <p className="text-sm text-stone-500">
          Aucun permis complexe nécessaire pour le moment.
        </p>
        <p className="text-[11px] text-stone-400 mt-1">
          Les permis complémentaires apparaîtront ici en fonction des risques cochés (blocs E à O).
        </p>
      </div>
    );
  }

  const byType = Object.fromEntries((linkedPermits || []).map((p) => [p.type, p]));
  const totalLinked = requiredTypes.filter((t) => byType[t]).length;
  const globalPct = totalLinked === 0
    ? 0
    : Math.round(
        requiredTypes.reduce((sum, t) => sum + (byType[t] ? computeCompletion(byType[t]) : 0), 0) / requiredTypes.length
      );

  return (
    <div data-testid="required-permits-block" className="space-y-3">
      <div className="flex items-start gap-3 p-3 bg-[#DA291C]/10 border-l-4 border-[#DA291C]">
        <AlertOctagon className="w-5 h-5 text-[#DA291C] flex-shrink-0 mt-0.5" />
        <div className="text-xs leading-snug flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="font-bold uppercase tracking-wider text-[#DA291C]">
              Permis complexe{requiredTypes.length > 1 ? 's' : ''} à compléter — {requiredTypes.length}
            </div>
            {totalLinked > 0 && (
              <div className="flex items-center gap-2">
                <CompletionRing percent={globalPct} size={32} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-600">Global</span>
              </div>
            )}
          </div>
          <div className="text-stone-700 mt-1">
            Cliquez sur chaque carte ci-dessous pour renseigner le permis. Le numéro chronologique racine est partagé (suffixe par type).
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {requiredTypes.map((t) => {
          const meta = PERMIT_TYPES.find((p) => p.id === t) || { id: t, label: t, short: t.slice(0, 2).toUpperCase(), description: '', color: 'bg-stone-100' };
          const linked = byType[t];
          const pct = linked ? computeCompletion(linked) : 0;
          const handleClick = () => {
            if (onOpen) onOpen(linked, parentId, t);
          };
          return (
            <button
              key={t}
              type="button"
              data-testid={`required-permit-${t}`}
              onClick={handleClick}
              className="text-left border-2 border-[#DA291C] bg-[#DA291C]/5 p-4 transition-all hover:bg-[#DA291C]/15 hover:shadow-md cursor-pointer"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 ${meta.color} border`}>
                  {meta.short}
                </span>
                {linked ? (
                  <CompletionRing percent={pct} />
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5">
                    à créer
                  </span>
                )}
              </div>
              <div className="text-sm font-display font-bold leading-tight text-[#09090B]">
                {meta.label}
              </div>
              <div className="text-[11px] text-stone-600 mt-1.5 leading-snug">
                {meta.description}
              </div>
              {linked ? (
                <div className="mt-3 pt-2 border-t border-[#DA291C]/30 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-[#DA291C]">
                  <span>{pct === 100 ? 'Permis complet' : `Compléter (${pct}%)`}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              ) : (
                <div className="mt-3 pt-2 border-t border-[#DA291C]/30 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-amber-700">
                  <span>Cliquer pour créer + compléter</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
